import { useParams } from "react-router-dom";
import { useStateWithSearchParams } from "../../../hooks";
import {
  useGenerateAccountPlansCSVForCampaignMutation,
  useGetAccountsPlanListQuery,
} from "../../../api";
import { useMemo, useState } from "react";
import {
  AccountPlanStatus,
  ModalSize,
  PlaybookPlanNames,
} from "../../../types/enums";
import { ExportCSVWithInfinityScrollModal } from "./ExportCSVWithInfinityScrollModal";

type SearchParams = {
  sizes?: string;
  industries?: string;
  regions?: string;
  search?: string;
  sort?: string;
  page?: string;
  per_page?: string;
  selected?: string;
  limit?: string;
  date?: string;
};

const PER_PAGE = 25;
const START_PAGE = 0;

export const ExportAccountPlansFromCampaignModal = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  const { campaign_id } = useParams();
  const [page, setPage] = useState<number>(START_PAGE);
  const [search, setSearch] = useState<string>("");

  const { searchDict } = useStateWithSearchParams<SearchParams>({
    search: undefined,
  });

  const [generateAccountPlansCSV, { isLoading: isGenerationCSV }] =
    useGenerateAccountPlansCSVForCampaignMutation();

  const {
    data: companies,
    isLoading,
    isFetching,
  } = useGetAccountsPlanListQuery(
    {
      campaign_id: campaign_id!,
      page,
      per_page: PER_PAGE,
      search: search || searchDict.search,
      status_filter: AccountPlanStatus.completed,
    },
    { refetchOnMountOrArgChange: true },
  );

  const submitHandler = (
    account_ids: string[],
    fileName: string,
    isSelectedAll: boolean,
  ) => {
    return generateAccountPlansCSV({
      campaign_id: campaign_id!,
      account_ids,
      file_name: fileName.trim(),
      include_all: isSelectedAll,
    }).unwrap();
  };

  const items = useMemo(
    () =>
      (companies?.items || [])?.map((company) => ({
        ...company,
        name: company.account_name,
        id: company.account_id,
        is_analyzed: true,
      })),
    [companies],
  );

  const setSearchHandler = (value: string) => {
    setSearch(value);
    setPage(START_PAGE);
  };

  return (
    <ExportCSVWithInfinityScrollModal
      title={`Export ${PlaybookPlanNames.account_plan} Links to CSV`}
      description={`For which accounts would you like to export ${PlaybookPlanNames.account_plan} links?`}
      items={items}
      isLoading={isGenerationCSV || isLoading}
      onSubmit={submitHandler}
      onClose={onClose}
      submitBtnText={"Export Links"}
      page={page}
      loadMore={() => setPage((prev) => prev + 1)}
      isFetching={isFetching}
      canLoadMore={!!companies && companies?.total_pages > page + 1}
      setSearchHandler={setSearchHandler}
      startPage={START_PAGE}
      maxWidth={ModalSize.mid}
    />
  );
};
